import ConfirmDialog from "designSystem/components/dialog/ConfirmDialog";
import ErrorDialog from "designSystem/components/dialog/ErrorDialog";
import PopupDialog from "designSystem/components/dialog/PopupDialog";
import { useSyncLocalStore } from "designSystem/hooks";
import { FlexRowCenter } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { ButtonEnum } from "designSystem/types/button-types";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, ReactNode, useEffect } from "react";
import Button from "../button/Button";
import { GridRow } from "../grid/GridContainer";
import Message, { MessageProps } from "../message/Message";
import OverflowText, { OverflowTextProps } from "../text/OverflowText";
import { PopupDialogProps } from "./PopupDialog";
import SaveDialogStore from "./store/SaveDialogStore";

export interface SaveDialogProps extends Omit<PopupDialogProps, "children"> {
	/** message in the bottom left hand corner */
	actionMessage?: string;

	/** show custom buttons */
	customButtons?: ReactNode;

	/** props to display a message on top of the main content */
	customMessageProps?: MessageProps;

	/** props to display overflow text on top of the main content */
	customOverflowTextProps?: OverflowTextProps;

	/** optional custom store to use instead of creating it internal with defaults */
	customStore?: SaveDialogStore;

	/** child components of dialog (leaving possibility for extra content like checkboxes, etc.) */
	children?: {
		/** the dialog header */
		header?: ReactNode;

		/** the main portion of the dialog */
		main?: ReactNode;
	};

	/** disable save button */
	disableSave?: boolean;

	/** callback function to called when cancel button pressed to close the dialog */
	onCancel?: () => void;

	/** callback function to close the dialog (called when either Save or Cancel is clicked) */
	onClose?: () => void;

	/** true if content of dialog has changed that will prompt a notification when closed */
	confirmClose?: boolean;

	/** callback function to save; return true to close the dialog */
	onSave?: () => Promise<boolean>;

	/** callback function to validate user input; return true to proceed to onSave; throw error to display error message */
	onValidate?: () => Promise<boolean>;
}

const SaveDialog: FunctionComponent<SaveDialogProps> = observer((props) => {
	const loc = useLocale();

	const {
		actionMessage,
		children,
		confirmClose,
		customButtons,
		customMessageProps,
		customOverflowTextProps,
		customStore,
		dataCsiAutomation,
		disableSave,
		onCancel,
		onClose,
		onSave,
		onValidate,
		...otherProps
	} = props;

	const store = customStore || useSyncLocalStore(() => new SaveDialogStore());

	useEffect(() => {
		store.setProps(props);
	}, [
		confirmClose,
		onCancel,
		onClose,
		onSave,
		onValidate,
	]);

	const {
		confirming,
		errorOnSave,
		errorOnValidate,
		onCancelDialog,
		onCloseDialog,
		onSaveDialog,
		saving,
		setConfirming,
		setErrorOnSave,
	} = store;
	
	const saveAutomation = dataCsiAutomation ? `button-Save-${dataCsiAutomation}` : undefined;
	const cancelAutomation = dataCsiAutomation ? `button-Cancel-${dataCsiAutomation}` : undefined;

	return (
		<PopupDialog
			onClose={onCloseDialog}
			showClose
			{...otherProps}
		>
			{{
				header: children?.header,
				main: children?.main,
				message: (
					<>
					  {errorOnValidate && (
							<Message
								titleText={loc.getErrorMessage(errorOnValidate)}
								variant="error"
							/>
						)
					  }
						{customMessageProps && (
							<Message {... customMessageProps} />
						)}
						{customOverflowTextProps && (
							<OverflowText {... customOverflowTextProps} />
						)}
					</>
				),
				buttons: (
					<>
						<GridRow
							alignItems="center"
							flexWrap="nowrap"
							growChild={0}
							spacing={2}
							textAlign="left"
						>
							<OverflowText text={actionMessage} />
							{customButtons ? (
								<>
									{customButtons}
								</>
							) : (
								<FlexRowCenter gap={2}>
									<Button
										data-csi-automation={saveAutomation}
										design="primary"
										disabled={saving || disableSave}
										onClick={onSaveDialog}
										res={ButtonEnum.BUTTON_SAVE}
									/>
									<Button
										data-csi-automation={cancelAutomation}
										design="link"
										disabled={saving}
										onClick={onCancelDialog}
										res={ButtonEnum.BUTTON_CANCEL}
									/>
								</FlexRowCenter>
							)}
						</GridRow>
						<ConfirmDialog
							confirmRes={ButtonEnum.BUTTON_CLOSE}
							messageRes="ConfirmCloseWithChange"
							onCancel={() => setConfirming(false)}
							onConfirm={onCancelDialog}
							open={confirming}
							titleRes="ConfirmCloseTitle"
						/>
						<ErrorDialog
							error={errorOnSave}
							onClose={() => setErrorOnSave(null)}
							open={!!errorOnSave}
						/>
					</>
				),
			}}
		</PopupDialog>
	);
});

export default SaveDialog;
